/**
 * What to tell the user when login / connection fails: one plain sentence + what to do, by kind (packages/shared/src/errors.ts);
 * the raw message is kept for "详情" (Details). field: which login form input the hint attaches below.
 */
import { classifyError, type ErrorKind } from '@momomail/shared';
import { language, t, tMessage } from './i18n';

export interface FriendlyError {
  kind: ErrorKind;
  /** One sentence: what happened */
  title: string;
  /** What to do */
  hint: string;
  /** Raw message (details) */
  detail: string;
  /** Which input it relates to */
  field: 'url' | 'username' | 'password' | null;
  /** warn: most likely user input error / will resolve after a moment; error: connection or server problem */
  severity: 'warn' | 'error';
  /** The raw message is worth showing (network, certificate, server and the like); for wrong password, the raw message is the same sentence from the backend, so no "详情" (Details) */
  technical: boolean;
}

const TECHNICAL = new Set<ErrorKind>(['offline', 'dns', 'unreachable', 'timeout', 'tls', 'address', 'not-momomail', 'local-mode', 'server', 'version']);

export function friendlyError(error: unknown): FriendlyError {
  const online = typeof navigator === 'undefined' || navigator.onLine !== false;
  const { kind, detail } = classifyError(error, online);
  const f = (title: string, hint: string, field: FriendlyError['field'], severity: FriendlyError['severity'] = 'error'): FriendlyError => ({ kind, title, hint, detail, field, severity, technical: TECHNICAL.has(kind) });
  switch (kind) {
    case 'offline':
      return f(t('这台设备没有联网'), t('连上网络后再试。'), null);
    case 'dns':
      return f(t('找不到这个服务器地址'), t('检查地址有没有拼错；域名如果是刚设置的，可能还没生效。'), 'url');
    case 'unreachable':
      return f(t('连不上服务器'), t('检查地址和网络；服务器可能没在运行，或者被防火墙、代理挡住了。'), 'url');
    case 'timeout':
      return f(t('服务器没有回应'), t('网络可能很慢或者服务器太忙，稍后再试。'), 'url', 'warn');
    case 'tls':
      return f(t('安全连接没能建立'), t('服务器的 HTTPS 证书有问题，或者网络里的代理 / VPN 在干扰加密连接。换个网络试试，或者联系管理员。'), 'url');
    case 'address':
      return f(t('服务器地址不对'), t('填完整的地址，比如 https://mail.example.com；远程服务器要用 https://。'), 'url', 'warn');
    case 'not-momomail':
      return f(t('这个地址不是馍馍邮箱服务器'), t('确认填的是管理员给你的服务器地址，不是落地页或别的网站。'), 'url', 'warn');
    case 'local-mode':
      return f(t('这个服务器不接受登录'), t('它是别人电脑上的本机模式；请连部署在服务器上的馍馍邮箱。'), 'url', 'warn');
    case 'credentials':
      return f(t('用户名或密码不对'), t('注意大小写；忘了密码可以点"忘记密码"，或者找管理员。'), 'password', 'warn');
    case 'rate-limited':
      return f(t('密码输错太多次了'), t('等几分钟再试。'), 'password', 'warn');
    case 'disabled':
      return f(t('这个账号已经停用'), t('请联系管理员。'), 'username');
    case 'server':
      return f(t('服务器出错了'), t('稍后再试；一直这样请联系管理员。'), null);
    case 'version':
      return f(t('应用和服务器的版本不兼容'), t('把应用更新到最新版，或者请管理员更新服务器。'), null);
    default:
      // Other messages from the backend (already plain sentences): translate and use directly
      return f(tMessage(detail) || t('没能完成'), t('再试一次；一直不行请把详情发给管理员。'), null);
  }
}

/** One line (for toasts and inline hints): what happened + what to do. */
export function friendlyText(error: unknown): string {
  const e = friendlyError(error);
  return e.kind === 'unknown' ? e.title : `${e.title}${language() === 'zh' ? '：' : ': '}${e.hint}`;
}
